import React from 'react';
import Button from 'components/ButtonDeprecated';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import { CardElement } from 'react-stripe-elements';
import CoachingHeading from 'coaching/components/CoachingHeading';
import { CoachingReservation } from 'coaching/containers/GetAvailableReservationsContainer';
import Copy from 'components/Copy';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Label from 'components/Label';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';

type Props = {
  childName?: string;
  purchaseForConnectedChild: boolean;
  reservation?: CoachingReservation;
  userTimezone: string;
  onCardInputCompleted: () => void;
  disabledContinueButton: boolean;
  onSubmitPayment: () => void;
  submittingPayment: boolean;
  isStudent: boolean;
  cardFailureReason: null | string;
  selectedSubject?: string;
};

export default function CoachingSelfServePaymentView(props: Props) {
  const eventPrefix = 'coaching.self_schedule_purchase.review';
  const trackEvent = (key: string) => {
    EventTracking.trackKey(`${eventPrefix}.${key}`);
  };

  React.useEffect(() => {
    trackEvent('view');
  }, []);

  const {
    childName,
    reservation,
    userTimezone,
    disabledContinueButton,
    onSubmitPayment,
    purchaseForConnectedChild,
    submittingPayment,
    isStudent,
    cardFailureReason,
    selectedSubject,
  } = props;

  if (!childName || !reservation || !selectedSubject) {
    return (
      <ErrorReporter error="CoachingSelfServePaymentView - tried to render without selecting a student or reservation">
        Oops - looks like we're missing some information. Please go back and complete all form
        fields.
      </ErrorReporter>
    );
  }

  const sharedProps = {
    selectedSubject,
    childName,
    reservation,
    userTimezone,
    isStudent,
  };

  return (
    <React.Fragment>
      <PlanAndSessionDetails {...sharedProps} />
      <Row size="xxsmall">
        <Label htmlFor="cardnumber">Credit or debit card</Label>
        <CardElement
          data-test="credit-card-input"
          className={`CoachingIsolatedView-cardInput ${
            cardFailureReason ? 'CoachingIsolatedView-cardInput--error' : ''
          }`}
          onChange={(e: stripe.elements.ElementChangeResponse) => {
            if (e.complete && !e.error) {
              props.onCardInputCompleted();
            }
          }}
          onFocus={() => trackEvent('credit_card_input.click')}
        />
      </Row>
      <Row size="small">
        {/* nbsp is to hold space and prevent UI from jumping when an error appears */}
        <Text color="apple" data-test="credit-card-failure-reason">
          {cardFailureReason}&nbsp;
        </Text>
      </Row>
      <Row size="small">
        <Button
          data-test="payment-submit-button"
          eventKey={`${eventPrefix}.confirm.click`}
          color="koolaid"
          disabled={disabledContinueButton}
          onClick={onSubmitPayment}
          loading={submittingPayment}>
          Confirm
        </Button>
      </Row>
      <LegalText
        isStudent={isStudent}
        purchaseForConnectedChild={purchaseForConnectedChild}
        eventPrefix={eventPrefix}
      />
    </React.Fragment>
  );
}

function LegalText(props: {
  purchaseForConnectedChild: boolean;
  eventPrefix: string;
  isStudent: boolean;
}) {
  const { purchaseForConnectedChild, eventPrefix, isStudent } = props;
  if (purchaseForConnectedChild) {
    return (
      <Text size="xsmall" color="graphite">
        By clicking “Confirm session”, you consent for your child to use Remind for the purpose of
        Remind Tutoring.
      </Text>
    );
  }

  return (
    <Text size="xsmall" color="graphite">
      By clicking “Confirm session”, you consent {isStudent ? '' : 'for your child'} to use Remind
      for the purpose of Remind Tutoring. We collect {isStudent ? 'your' : 'their'} name, subject of
      interest, session audio/video, and computer session information for quality and safety
      purposes. Remind never sells or shares this information. View our{' '}
      <Link
        eventKey={`${eventPrefix}.privacy_policy.click`}
        href={LinkConstants.PRIVACY_POLICY}
        target="_blank"
        underline
        weight="semibold"
        color="ink">
        Privacy Policy.
      </Link>
    </Text>
  );
}

type DetailsProps = {
  selectedSubject: string;
  childName: string;
  reservation: CoachingReservation;
  userTimezone: string;
  isStudent: boolean;
};

function PlanAndSessionDetails({
  selectedSubject,
  childName,
  reservation,
  userTimezone,
}: DetailsProps) {
  return (
    <>
      <CoachingHeading size="small">Let’s book that free session</CoachingHeading>
      <Row size="small">
        <Copy align="left" size="small">
          After you try your free first session, you can continue with your coach (or a different
          coach) at <Text weight="semibold">$40 per session</Text>. Skipping, rescheduling, or
          canceling sessions is easy.
        </Copy>
      </Row>
      <Row size="small">
        <Box color="eraser" textAlign="left" noShadow>
          <BoxBody size="xsmall">
            <Row size="xxsmall">
              <Text size="small" weight="semibold">
                {`${selectedSubject} for ${childName}`}
              </Text>
            </Row>
            <Row size="xxsmall">
              <Text size="small">
                One {reservation.endDateTime.diff(reservation.startDateTime, 'minutes')}-minute
                session per week
              </Text>
            </Row>
          </BoxBody>
          <BoxBody light size="xsmall" rounded>
            <Row size="xxsmall">
              <Text size="small" color="graphite">
                Free first session
              </Text>
            </Row>
            <Row size="xxsmall">
              <TextWithIcon icon="clock" size="small" weight="semibold">
                {reservation.startDateTime.tz(userTimezone).format('ddd, MMM D [at] LT (z)')}
              </TextWithIcon>
            </Row>
          </BoxBody>
        </Box>
      </Row>
    </>
  );
}
